import {
  AdminPanelSettingsOutlined,
  HelpOutline,
  LogoutOutlined,
  MenuSharp,
  PersonOutlined,
  WorkspacePremiumOutlined,
} from '@mui/icons-material'
import {
  Avatar,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useGetCustomersWithAdminUserPermission } from 'src/api/customer'
import LanguageIconMenu, { OptionsProps } from 'src/components/LanguageIconMenu'
import { permissionValue } from 'src/enums/permissionValue'
import { ICustomer } from 'src/generated-types'
import { useLocalStorage } from 'src/hooks/local-storage'
import i18n from 'src/i18n'
import { MUIThemeColors } from 'src/mui-theme'
import { fontColorFromBackground, getThemeColor, userColor } from 'src/utils'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { languages } from 'src/utils/languages'
import { AppRoutesNavigation } from '../../app-routes'
import logo from '../../assets/lovold_logo.png'
import { useFeatureFlagsCtx, useSidebarCtx, useUserCtx } from '../../hooks/context-hooks'
import { hasMainNavAccess } from 'src/utils/appFeatureAccess'
import { mainTopBarItems } from '../../screens/main-top-bar-items'
import { Flex } from '../../styles/flexComponent'
import { userManager } from '../../user-manager'
import { NotificationBell } from '../../components/notifications/NotificationBell'
import { useCurrentCustomerId } from '../../hooks/useCurrentCustomerId'
import { AdminPanelMenuIcon } from './AdminPanelMenuIcon'
import {
  LogoContainer,
  NavigationContainer,
  PaperProperties,
  StyledListItemIcon,
  StyledMenuItemTypography,
  StyledMenuItem,
  TopBarIcons,
} from './Index.style'
import { Link } from 'react-router-dom'

const SYSTEM_ADMIN_PERMISSION = BigInt(permissionValue.SystemAdminPermission)

export const TopBar = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { setCollapsibleSidebarOpen } = useSidebarCtx()
  const { t: translate } = useTranslation(['header', 'common'])
  const { user } = useUserCtx()
  const currentCustomerId = useCurrentCustomerId()
  const { isIntegrationEnabled } = useFeatureFlagsCtx()
  const [_, setStorageItem] = useLocalStorage('languageCode', 'en')
  const isSidebarHidden = ['locality', 'service-station', 'storage', 'project'].includes(pathname.split('/')[1])
  const theme = useTheme()
  const isLarge = useMediaQuery<any>(theme.breakpoints.up(2000))
  const isSmall = useMediaQuery<any>(theme.breakpoints.down('md'))
  const { data: userCustomers, isLoading } = useGetCustomersWithAdminUserPermission()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const onLogout = () => {
    userManager.signoutRedirect()
  }

  function changeGlobalLanguage(e) {
    const lang = e.target.value.toString()
    i18n.changeLanguage(lang)
    setStorageItem(lang)
  }

  const userFirstLetters = user
    ? (user?.firstName?.charAt(0).toUpperCase() || '') + (user?.lastName?.charAt(0).toUpperCase() || '')
    : '-'
  const isSuperAdmin = useMemo(() => {
    return (BigInt(user?.permission ?? 0) & SYSTEM_ADMIN_PERMISSION) === SYSTEM_ADMIN_PERMISSION
  }, [user])

  const accountMenuIconSx = {
    fontSize: 24,
    width: 24,
    height: 24,
    color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  }

  return (
    <>
      <Toolbar>
        {user && !isSidebarHidden && !isLarge && pathname !== AppRoutesNavigation.UnAuth && (
          <MenuSharp fontSize={'large'} onClick={() => setCollapsibleSidebarOpen(true)} />
        )}
        <LogoContainer sx={{ paddingLeft: { lg: '12px', md: '12px' } }} ml={!isSmall ? 1 : 0}>
          <Link to={AppRoutesNavigation.Localities.basePath}>
            <img
              width={193}
              src={logo}
              alt="Logo"
              draggable={false}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
              }}
            />
          </Link>
        </LogoContainer>
        <Flex.Row spacing={1} justifyContent={'flex-end'} alignItems={'center'} height={80}>
          <Stack direction={'row'} alignItems={'center'}>
            <Flex.Row paddingRight={'10px'} alignItems={'center'}>
              {user &&
                mainTopBarItems.map((nav, index) => {
                  const isActive = !!pathname && [nav.path, nav.main].includes(`/${pathname.split('/')[1]}`)
                  return (
                    hasMainNavAccess(user, nav, isIntegrationEnabled) && (
                      <Link to={nav.path}>
                      <NavigationContainer
                        key={index}
                        sx={{ display: { xs: 'none', sm: 'flex' } }}
                        $active={isActive}
                      >
                        <nav.Icon style={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
                        <Typography
                          color={
                            isActive
                              ? getThemeColor(theme, MUIThemeColors.primaryMain)
                              : getThemeColor(theme, MUIThemeColors.black)
                          }
                          variant="subtitle1"
                          marginLeft={1}
                          paddingTop={'9px'}
                          fontWeight={600}
                          sx={{ userSelect: 'none', display: { sm: 'none', md: 'none', lg: 'flex' } }}
                        >
                          {translate(nav.name)}
                        </Typography>
                      </NavigationContainer>
                      </Link>
                    )
                  )
                })}
            </Flex.Row>
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              {user && currentCustomerId > 0 && (
                <Tooltip title={translate('notifications', { ns: 'header' }) as string}>
                  <span>
                    <NotificationBell customerId={currentCustomerId} />
                  </span>
                </Tooltip>
              )}
              {user && (
                <Tooltip title={translate('contact-support', { ns: 'header' }) as string}>
                  <TopBarIcons
                    onClick={() => {
                      if ('FreshworksWidget' in window) {
                        // @ts-ignore
                        window.FreshworksWidget('open')
                      }
                    }}
                  >
                    <HelpOutline sx={{ fontSize: '32px', color: theme.palette.primaryMain[theme.palette.mode] }} />
                  </TopBarIcons>
                </Tooltip>
              )}
              {user && isSuperAdmin && (
                <Tooltip title={translate('admin-panel', { ns: 'header' }) as string}>
                  <TopBarIcons sx={{ display: { xs: 'none', lg: 'flex' } }}>
                    <AdminPanelSettingsOutlined
                      sx={{ fontSize: '32px', color: theme.palette.primaryMain[theme.palette.mode] }}
                      onClick={() => navigate(AppRoutesNavigation.Admin.customers)}
                    />
                  </TopBarIcons>
                </Tooltip>
              )}
              <LanguageIconMenu name="language" options={languages as OptionsProps[]} onChange={changeGlobalLanguage} />
              <Tooltip title={translate(user?.firstName + ' ' + user?.lastName, { ns: 'header' }) as string}>
                <IconButton
                  onClick={handleClick}
                  size="small"
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <Avatar
                    src={user?.profileImage}
                    sx={{
                      width: 36,
                      height: 36,
                      color: fontColorFromBackground(userColor({ userId: Number(user?.id), ...user })),
                      backgroundColor: userColor({ userId: Number(user?.id), ...user }),
                    }}
                  >
                    {user?.profileImage == undefined ? userFirstLetters : ''}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={PaperProperties}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              sx={{
                py: 1.25,
                px: 2,
                gap: 1.5,
                alignItems: 'center',
                display: { xs: 'none', sm: 'flex' },
                textTransform: 'capitalize',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                {getUserDetail(user, UserDetail.FullName)}
              </Typography>
            </MenuItem>
            <Divider sx={{ display: { xs: 'none', sm: 'flex' } }} />
            {user && [
              <StyledMenuItem
                key="profile"
                onClick={() => navigate(AppRoutesNavigation.Profile, { state: { prevPath: pathname } })}
              >
                <StyledListItemIcon sx={{ color: 'text.secondary' }}>
                  <PersonOutlined sx={accountMenuIconSx} />
                </StyledListItemIcon>
                <StyledMenuItemTypography variant="body1">
                  {translate('manageProfile', { ns: 'header' })}
                </StyledMenuItemTypography>
              </StyledMenuItem>,
              userCustomers && userCustomers.length > 0 ? (
                <StyledMenuItem
                  key="admin-panel"
                  onClick={() => {
                    const storedCustomerId = localStorage.getItem('selectedCustomerAdminCustomerId')
                    let selectedCustomer: ICustomer | undefined = undefined
                    if (storedCustomerId && userCustomers) {
                      selectedCustomer = userCustomers.find(customer => customer.id.toString() === storedCustomerId)
                    } else {
                      selectedCustomer = userCustomers ? userCustomers[0] : undefined
                    }
                    if (selectedCustomer) {
                      if (selectedCustomer.isGlobalServiceStationProvider) {
                        navigate(AppRoutesNavigation.Customer.serviceStations(selectedCustomer.id))
                      } else {
                        navigate(AppRoutesNavigation.Customer.localities(selectedCustomer.id))
                      }
                    }
                  }}
                >
                  <StyledListItemIcon sx={{ color: 'text.secondary' }}>
                    <AdminPanelMenuIcon color={getThemeColor(theme, MUIThemeColors.secondaryMain)} />
                  </StyledListItemIcon>
                  <StyledMenuItemTypography variant="body1">
                    {translate('admin-panel', { ns: 'header' })}
                  </StyledMenuItemTypography>
                </StyledMenuItem>
              ) : null,
              <StyledMenuItem
                key="super-admin"
                onClick={() => navigate(AppRoutesNavigation.Admin.customers)}
                sx={{ display: { xs: 'flex', lg: 'none' } }}
              >
                <StyledListItemIcon sx={{ color: 'text.secondary' }}>
                  <AdminPanelMenuIcon color={getThemeColor(theme, MUIThemeColors.secondaryMain)} />
                </StyledListItemIcon>
                <StyledMenuItemTypography variant="body1">
                  {translate('superAdmin', { ns: 'common' })}
                </StyledMenuItemTypography>
              </StyledMenuItem>,
              <StyledMenuItem
                key="certificate-database"
                onClick={() => navigate(AppRoutesNavigation.CertificateDatabase)}
              >
                <StyledListItemIcon sx={{ minWidth: 40, justifyContent: 'center', alignItems: 'center' }}>
                  <WorkspacePremiumOutlined sx={accountMenuIconSx} />
                </StyledListItemIcon>
                <StyledMenuItemTypography variant="body1">
                  {translate('certificate-database', { ns: 'header' })}
                </StyledMenuItemTypography>
              </StyledMenuItem>,
            ]}
            <Divider />
            <StyledMenuItem onClick={onLogout}>
              <StyledListItemIcon sx={{ color: 'text.secondary' }}>
                <LogoutOutlined sx={accountMenuIconSx} />
              </StyledListItemIcon>
              <StyledMenuItemTypography variant="body1">
                {translate('logout', { ns: 'header' })}
              </StyledMenuItemTypography>
            </StyledMenuItem>
          </Menu>
        </Flex.Row>
      </Toolbar>
    </>
  )
}
